import api from "helpers/ApiClient";
import { checkTokenIsExpired } from "helpers/checkTokenExpires";
import { toast } from "helpers/toastWrapper";

export const getConversation = ({
	page = 1,
	setConversation,
	conversationId,
}) => {
	setConversation((prev) => {
		return {
			...prev,
			loading: true,
		};
	});

	api.get(`/message`, {
		params: {
			id: conversationId,
			page: page,
			reverse: true,
		},
		headers: {
			Accept: "application/api.1.1+json",
		},
		fromConversation: page !== 1,
	})
		.then(({ data } = {}) => {
			if (!data) return;

			const { user, messages, message_hash, video, pagination, url_ws } =
				data;

			setConversation((prev) => {
				return {
					websocketUrl: url_ws,
					message_hash,
					messages: [...messages, ...prev.messages],
					pagination: pagination.pagination,
					user: {
						token: user.token,
						id: user.id,
						login: user.login,
						avatar: user.avatar,
						private_data: user.private_data,
						profile_status: user.profile_status,
						status_vip: user.status_vip,
						online: user.online,
						editor: {
							disabled: user.editor.disabled,
							info: user.editor.info,
						},
					},
					video,
					loading: false,
					forceRefresh: prev.forceRefresh,
				};
			});
		})
		.catch((error) => {
			setConversation((prev) => {
				return {
					...prev,
					loading: false,
				};
			});
			if (error?.response?.data?.error) {
				toast.error(`${error.response.data.error}`);
			}
		});
};

export const sendMessageAfterCheckToken = ({
	token,
	text,
	attachment,
	sendWebsocketMessage,
	clearEditor,
	setMessageSending,
	scrollToBottom,
	setConversation,
	sessionId,
}) => {
	const handleSendWebsocketMessage = (data) => {
		sendWebsocketMessage({
			action: "send_new_message",
			message_id: data.id,
			message_text_api: data.message,
			date: data.date,
			send: data.send,
			attachment: {
				attachment_id: data.file_id,
				attachment_file: data.file_hash,
				file_big: data.file_big,
				file_small: data.file_small,
			},
			sessionId,
		});
	};

	if (attachment) {
		const formData = new FormData();
		formData.append(
			"data",
			JSON.stringify({
				token,
				message: text,
				local_id: null,
			})
		);
		formData.append("attachment", attachment);
		api.get("/system/token")
			.then(({ data: { token: _uploadToken } }) => {
				formData.append("token", _uploadToken);

				return api
					.post(`/message/token/file`, formData)
					.then(({ data } = {}) => {
						if (!data) {
							return;
						}

						setConversation((prev) => {
							return {
								...prev,
								messages: [...prev.messages, data],
							};
						});
						handleSendWebsocketMessage(data);
						clearEditor();
					})
					.catch((error) => {
						toast.error(`${error.response.data.error}`);
					})
					.finally(() => {
						setMessageSending(false);
						scrollToBottom();
					});
			})
			.catch((error) => {
				toast.error(`${error.response.data.error}`);
			});
	} else {
		api.post(`/message/token`, {
			token,
			message: text,
			local_id: null,
		})
			.then(({ data } = {}) => {
				if (!data) {
					return;
				}

				setConversation((prev) => {
					return { ...prev, messages: [...prev.messages, data] };
				});
				handleSendWebsocketMessage(data);
				clearEditor();
			})
			.catch((error) => {
				toast.error(`${error.response.data.error}`);
			})
			.finally(() => {
				setMessageSending(false);
				scrollToBottom();
			});
	}
};

export const onSend = ({
	text,
	attachment,
	clearEditor,
	setMessageSending,
	conversation,
	conversationId,
	setConversation,
	sendWebsocketMessage,
	scrollToBottom,
	sessionId,
}) =>
	new Promise((resolve) => {
		if (text.length > 0 || attachment) {
			setMessageSending(true);

			checkTokenIsExpired(conversation.message_hash).then((isExpired) => {
				if (isExpired && typeof window !== "undefined") {
					api.get(`/message/token?id=${conversationId}`).then(
						({ data } = {}) => {
							setConversation((prev) => {
								return {
									...prev,
									message_hash: data.message_hash,
								};
							});
							resolve(
								sendMessageAfterCheckToken({
									token: data.message_hash,
									text,
									attachment,
									sendWebsocketMessage,
									clearEditor,
									setMessageSending,
									scrollToBottom,
									setConversation,
									sessionId,
								})
							);
						}
					);
				} else {
					if (conversation.message_hash) {
						resolve(
							sendMessageAfterCheckToken({
								token: conversation.message_hash,
								text,
								attachment,
								sendWebsocketMessage,
								clearEditor,
								setMessageSending,
								scrollToBottom,
								setConversation,
								sessionId,
							})
						);
					} else {
						api.get(`/message/token?id=${conversationId}`).then(
							({ data } = {}) => {
								setConversation((prev) => {
									return {
										...prev,
										message_hash: data.message_hash,
									};
								});
								resolve(
									sendMessageAfterCheckToken({
										token: data.message_hash,
										text,
										attachment,
										sendWebsocketMessage,
										clearEditor,
										setMessageSending,
										scrollToBottom,
										setConversation,
										sessionId,
									})
								);
							}
						);
					}
				}
			});
		}
		resolve(false);
	});

export const removeAttachment = ({
	setConversation,
	deletedMessageId,
	sendWebsocketMessage,
	sessionId,
}) => {
	api.delete(`/message/attachment`, {
		params: {
			id: deletedMessageId,
		},
	})
		.then(() => {
			setConversation((prev) => {
				return {
					...prev,
					forceRefresh: !prev.forceRefresh,
					messages: prev.messages.map((message) => {
						if (message.id === deletedMessageId) {
							return {
								...message,
								file_small: null,
								file_big: null,
								attachmentRemoving: false,
								attachmentIsRemoved: true,
							};
						}
						return message;
					}),
				};
			});

			sendWebsocketMessage({
				action: "remove_attachment",
				messageId: deletedMessageId,
				sessionId,
			});
		})
		.catch((error) => {
			toast.error(`${error.response.data.error}`);
		});
};

export const markAsReaded = (conversationId) => {
	api.put(
		`/message/read?id=${conversationId}`,
		{},
		{
			headers: {
				Accept: "application/api.1.1+json",
			},
		}
	).catch((error) => {
		console.log(error.response.data.error);
	});
};
export const createConversationBroadcast = ({
	conversation,
	setConversation,
	setVideoChat,
	publisherMD5Hash,
	enableDebug,
	captureAndSendMessage,
}) => {
	const dateFormat = (date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		const seconds = String(date.getSeconds()).padStart(2, "0");
		return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
	};
	const currentDate = dateFormat(new Date());

	api.post(`camera/conversation?userId=${conversation.user.id}`).then(
		({ data } = {}) => {
			if (!data) {
				return;
			}
			setVideoChat((prev) => {
				return {
					...prev,
					publisherHash: publisherMD5Hash,
					broadcastId: data?.broadcastId,
				};
			});
		}
	);
	if (enableDebug) {
		console.log("WEBSOCKET - SENT ->  stream_created");
	}

	captureAndSendMessage(currentDate);

	setConversation((prev) => {
		return {
			...prev,
			messages: [
				...prev.messages,
				{
					id: currentDate,
					message: [""],
					additional_status: 1,
					send: true,
					read: false,
					file_big: null,
					file_hash: null,
					file_id: null,
					file_small: null,
					date: currentDate,
				},
			],
		};
	});
};
